import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  Trash2, 
  Copy, 
  Key,
  Calendar,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import { ApiKey, CreateApiKeyRequest } from '@/types'
import { partnerService } from '@/services/partnerService'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Modal } from '@/components/organisms/modals/Modal'
import { cn, formatDate } from '@/utils'

interface ApiKeyManagementProps {
  className?: string
}

const ApiKeyManagement: React.FC<ApiKeyManagementProps> = ({ className }) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showKeySecretModal, setShowKeySecretModal] = useState(false)
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null)
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null)
  const [createForm, setCreateForm] = useState<CreateApiKeyRequest>({
    key_name: '',
    expires_at: ''
  })
  const [showCopied, setShowCopied] = useState(false)

  useEffect(() => {
    loadApiKeys()
  }, [])

  const loadApiKeys = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await partnerService.listApiKeys()
      setApiKeys(response.data)
    } catch (err) {
      if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You may not have permission to access Partner API features.')
      } else {
        setError('Failed to load API keys')
      }
      console.error('Error loading API keys:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateKey = async () => {
    if (!createForm.key_name.trim()) return

    setLoading(true)
    try {
      const response = await partnerService.createApiKey(createForm)
      setNewKeySecret(response.data.key_secret)
      setShowKeySecretModal(true)
      setCreateForm({ key_name: '', expires_at: '' })
      setShowCreateModal(false)
      await loadApiKeys()
    } catch (err) {
      setError('Failed to create API key')
      console.error('Error creating API key:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteKey = async () => {
    if (!keyToDelete) return

    setLoading(true)
    try {
      await partnerService.deleteApiKey(keyToDelete.key_id)
      setShowDeleteModal(false)
      setKeyToDelete(null)
      await loadApiKeys()
    } catch (err) {
      setError('Failed to delete API key')
      console.error('Error deleting API key:', err)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setShowCopied(true)
      setTimeout(() => setShowCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const getExpiryStatus = (expiresAt: string | null) => {
    if (!expiresAt) return { status: 'never', color: 'text-green-400' }
    
    const expiryDate = new Date(expiresAt)
    const now = new Date()
    
    if (expiryDate < now) {
      return { status: 'expired', color: 'text-red-400' }
    }
    
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysUntilExpiry <= 7) {
      return { status: 'expiring-soon', color: 'text-yellow-400' }
    }
    
    return { status: 'active', color: 'text-green-400' }
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">API Keys</h2>
          <p className="text-muted-foreground">Manage your API keys for accessing the platform</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Add API Key
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle size={16} />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* API Keys List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : apiKeys.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Key size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No API Keys</h3>
              <p className="text-muted-foreground mb-4">Create your first API key to start integrating with the platform</p>
              <Button onClick={() => setShowCreateModal(true)}>
                Create API Key
              </Button>
            </CardContent>
          </Card>
        ) : (
          apiKeys.map((key) => {
            const expiryStatus = getExpiryStatus(key.expires_at)
            return (
              <Card key={key.key_id} className="hover:bg-muted transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Key size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{key.key_name}</h3>
                        <p className="text-sm text-muted-foreground">ID: {key.key_id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {key.expires_at && (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar size={14} className={expiryStatus.color} />
                          <span className={expiryStatus.color}>
                            {formatDate(new Date(key.expires_at))}
                          </span>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setKeyToDelete(key)
                          setShowDeleteModal(true)
                        }}
                        className="flex items-center gap-2"
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New API Key"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="key-name">Key Name</Label>
            <Input
              id="key-name"
              type="text"
              value={createForm.key_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, key_name: e.target.value }))}
              placeholder="Enter a name for your API key"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expires-at">Expiration Date (Optional)</Label>
            <Input
              id="expires-at"
              type="datetime-local"
              value={createForm.expires_at}
              onChange={(e) => setCreateForm(prev => ({ ...prev, expires_at: e.target.value }))}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleCreateKey}
              disabled={!createForm.key_name.trim() || loading}
              className="flex-1"
            >
              {loading ? 'Creating...' : 'Create Key'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete API Key"
      >
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle size={20} />
            <AlertDescription>
              Are you sure you want to delete this API key? This action cannot be undone. The key will be permanently removed.
            </AlertDescription>
          </Alert>
          {keyToDelete && (
            <Card>
              <CardContent className="p-3">
                <p className="font-medium">{keyToDelete.key_name}</p>
                <p className="text-sm text-muted-foreground">ID: {keyToDelete.key_id}</p>
              </CardContent>
            </Card>
          )}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleDeleteKey}
              disabled={loading}
              variant="destructive"
              className="flex-1"
            >
              {loading ? 'Deleting...' : 'Delete Key'}
            </Button>
            <Button
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Key Secret Modal */}
      <Modal
        isOpen={showKeySecretModal}
        onClose={() => setShowKeySecretModal(false)}
        title="API Key Created"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <Alert>
            <AlertTriangle size={20} />
            <AlertDescription>
              Important: Copy your API key now. This is the only time you'll see your API key secret. Make sure to copy it now.
            </AlertDescription>
          </Alert>
          
          {newKeySecret && (
            <div className="space-y-2">
              <Label htmlFor="api-key-secret">API Key Secret</Label>
              <div className="relative">
                <Input
                  id="api-key-secret"
                  type="text"
                  value={newKeySecret}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  onClick={() => copyToClipboard(newKeySecret)}
                  variant="outline"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showCopied ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => setShowKeySecretModal(false)}
              className="flex-1"
            >
              I've Copied My Key
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ApiKeyManagement 